import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface PageData {
  id?: string;
  title: string;
  slug: string;
  content: string;
  meta_title: string;
  meta_description: string;
  is_published: boolean;
  show_in_navigation: boolean;
  navigation_order: number;
}

export default function PageEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewPage = id === 'new';

  const [loading, setLoading] = useState(!isNewPage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PageData>({
    title: '',
    slug: '',
    content: '',
    meta_title: '',
    meta_description: '',
    is_published: false,
    show_in_navigation: false,
    navigation_order: 0,
  });

  useEffect(() => {
    if (!isNewPage && id) {
      fetchPage(id);
    }
  }, [id, isNewPage]);

  const fetchPage = async (pageId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<PageData>(`/admin/pages/${pageId}`);
      setFormData(response);
    } catch (err) {
      setError('Failed to load page. Please try again.');
      console.error('Error fetching page:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: prev.slug === '' || prev.slug === generateSlug(prev.title)
        ? generateSlug(value)
        : prev.slug
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      if (isNewPage) {
        const response = await api.post<{ id: string }>('/admin/pages', formData);
        navigate(`/admin/pages/${response.id}`);
      } else {
        await api.put(`/admin/pages/${id}`, formData);
        navigate('/admin/pages');
      }
    } catch (err) {
      setError('Failed to save page. Please try again.');
      console.error('Error saving page:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading page...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/pages')}
          className="mb-4"
          aria-label="Back to pages"
        >
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Pages
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isNewPage ? 'Create New Page' : 'Edit Page'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card padding="lg">
              <div className="space-y-6">
                <Input
                  label="Page Title"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  required
                  aria-required="true"
                />

                <Input
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  helperText="URL-friendly version of the title"
                  required
                  aria-required="true"
                />

                <Textarea
                  label="Content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={12}
                  helperText="HTML content for the page"
                />
              </div>
            </Card>

            <Card title="SEO Settings" padding="lg">
              <div className="space-y-6">
                <Input
                  label="Meta Title"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  helperText="Leave empty to use page title"
                />

                <Textarea
                  label="Meta Description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows={3}
                  helperText="Brief description for search engines (150-160 characters recommended)"
                />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card title="Page Status" padding="lg">
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    aria-describedby="published-help"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Published</span>
                    <p id="published-help" className="text-xs text-gray-500">Make this page visible to visitors</p>
                  </div>
                </label>
              </div>
            </Card>

            <Card title="Navigation" padding="lg">
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_in_navigation}
                    onChange={(e) => setFormData({ ...formData, show_in_navigation: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    aria-describedby="nav-help"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Show in Navigation</span>
                    <p id="nav-help" className="text-xs text-gray-500">Display in main menu</p>
                  </div>
                </label>

                <Input
                  label="Navigation Order"
                  type="number"
                  value={formData.navigation_order}
                  onChange={(e) => setFormData({ ...formData, navigation_order: parseInt(e.target.value) || 0 })}
                  helperText="Lower numbers appear first"
                  min={0}
                />
              </div>
            </Card>

            <div className="sticky top-6">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                isLoading={saving}
                disabled={saving || !formData.title || !formData.slug}
              >
                <Save className="w-4 h-4 mr-2" aria-hidden="true" />
                {saving ? 'Saving...' : 'Save Page'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
